import { BtUtils } from "@/shared/sync/dataTypes/BtUtils";
import { UserDevice } from "@/shared/sync/dataTypes/UserDevice";
import { getDatabase, ref, DatabaseReference, off, DataSnapshot, onValue, set } from 'firebase/database';
import type { CloudManagerDelegate } from "./CloudManager";
import {
  CloudManagerAction,
  CloudManagerEventMessage,
  CloudManagerMessageType,
  CloudManagerRequestMessage,
  CloudManagerResponseMessage,
} from "@/shared/service-worker/CloudManagerMessageTypes";
import { CloudResponse, EmptyResponse } from "./CloudResponse";
import { CloudUrl } from "./CloudUrl";
import { FolderObject } from "@/shared/sync/dataTypes/FolderObject";
import { CloudManagerBase } from "./CloudManagerBase";

const FIREBASE_TOKEN_REQUEST_TIMEOUT_MS = 15_000;

function nextId(): string {
  return crypto.randomUUID();
}

export class CloudManagerClient extends CloudManagerBase implements CloudManagerDelegate {
  static get shared(): CloudManagerClient {
    if (!(globalThis as any).__cloudManagerClient) {
      (globalThis as any).__cloudManagerClient = new CloudManagerClient();
    }
    return (globalThis as any).__cloudManagerClient;
  }

  delegate?: CloudManagerDelegate;
  private eventPort?: MessagePort;
  private lastServiceWorker?: ServiceWorker;
  private uploadStartCallbacks = new Map<string, () => void>();
  private deleteCallbacks = new Map<string, (id: string) => Promise<void> | void>();

  private async ensureServiceWorkerRegistration(): Promise<ServiceWorkerRegistration | undefined> {
    if (typeof navigator === "undefined" || !("serviceWorker" in navigator)) {
      return undefined;
    }

    try {
      // const registration = await navigator.serviceWorker.getRegistration();

      // if (registration?.active || registration?.waiting || registration?.installing) {
      //   return registration;
      // }

      const version = process.env.REACT_APP_VERSION;

      return await navigator.serviceWorker.register(`/firebase-sw-${version}.mjs`, {
        type: "module",
        updateViaCache: "none",
        scope: "/",
      });
    } catch (error) {
      console.error("sync: failed to ensure service worker registration", error);
      return undefined;
    }
  }

  private async getServiceWorker(): Promise<ServiceWorker> {
    let registration = await this.ensureServiceWorkerRegistration();

    // Only an active worker is guaranteed to have evaluated the message router.
    // Posting to a freshly installing worker can silently lose the request,
    // leaving callers waiting forever (most visible during first login on mobile).
    let sw = registration?.active;

    if (!sw && registration) {
      try {
        await registration.update();
        sw = registration.active;
      } catch (error) {
        console.warn("sync: failed to update service worker registration", error);
      }
    }

    if (!sw && typeof navigator !== "undefined" && "serviceWorker" in navigator) {
      try {
        registration = await navigator.serviceWorker.ready;
        sw = registration.active;
      } catch (error) {
        console.warn("sync: failed to resolve ready service worker", error);
      }
    }

    if (!sw) {
      throw new Error("No active service worker for CloudManager communication");
    }

    if (this.lastServiceWorker && this.lastServiceWorker !== sw) {
      try {
        this.eventPort?.close();
      } catch (error) {
        console.warn("sync: failed to close previous CloudManager event port", error);
      }
      this.eventPort = undefined;
    }

    this.lastServiceWorker = sw;

    return sw;
  }

  private sendRequest(action: CloudManagerAction, ...args: unknown[]): Promise<any> {
    const requestId = nextId();

    const message: CloudManagerRequestMessage = {
      type: CloudManagerMessageType.Request,
      requestId,
      action,
      args,
    };

    return new Promise((resolve, reject) => {
      let channel: MessageChannel | undefined;
      let timeoutId: ReturnType<typeof setTimeout> | undefined;
      let settled = false;
      const finish = (callback: () => void) => {
        if (settled) return;
        settled = true;
        if (timeoutId !== undefined) {
          clearTimeout(timeoutId);
        }
        channel?.port1.close();
        callback();
      };

      // Token propagation is part of sign-in, so it must not hold the auth UI
      // forever while an iOS worker activates or if it terminates before
      // replying. Long-running upload/download requests retain their behavior.
      if (action === "setFirebaseToken") {
        timeoutId = setTimeout(() => {
          finish(() => reject(new Error("CloudManager Firebase token request timed out")));
        }, FIREBASE_TOKEN_REQUEST_TIMEOUT_MS);
      }

      void this.getServiceWorker().then((sw) => {
        if (settled) return;

        channel = new MessageChannel();
        channel.port1.onmessage = (event: MessageEvent<CloudManagerResponseMessage>) => {
          const payload = event.data;
          if (payload.requestId !== requestId) return;
          if (payload.ok) {
            finish(() => resolve(payload.result));
          } else {
            finish(() => reject(new Error(payload.error || "CloudManager request failed")));
          }
        };

        try {
          sw.postMessage(message, [channel.port2]);
        } catch (error) {
          finish(() => reject(error));
        }
      }).catch((error) => {
        finish(() => reject(error));
      });
    });
  }

  private async ensureEventSubscription(): Promise<void> {
    if (this.eventPort) return;

    const sw = await this.getServiceWorker();
    const channel = new MessageChannel();

    channel.port1.onmessage = (event: MessageEvent<CloudManagerEventMessage>) => {
      this.handleEvent(event.data);
    };

    sw.postMessage({ type: CloudManagerMessageType.Subscribe }, [channel.port2]);
    this.eventPort = channel.port1;
  }

  private handleEvent(event: CloudManagerEventMessage): void {
    switch (event.event) {
      case "cloudManagerDidStartUploadProject": {
        const callbackId = (event.payload as string | undefined) || undefined;
        if (!callbackId) break;
        const callback = this.uploadStartCallbacks.get(callbackId);
        if (callback) {
          callback();
          this.uploadStartCallbacks.delete(callbackId);
        }
        this.delegate?.cloudManagerDidStartUploadProject?.(callbackId);
        break;
      }
      case "cloudManagerDidUploadMetaData":
        this.delegate?.cloudManagerDidUploadMetaData();
        break;
      case "cloudManagerDidDetectMetaDataChange":
        this.delegate?.cloudManagerDidDetectMetaDataChange();
        break;
      case "cloudManagerDidReceivedCloudFolders":
        this.delegate?.cloudManagerDidReceivedCloudFolders((event.payload as any[]) || []);
        break;
      case "cloudManagerDidReceivedRecycleBinItems":
        this.delegate?.cloudManagerDidReceivedRecycleBinItems((event.payload as object) || {});
        break;
      case "cloudManagerDidDeleteProject": {
        const payload = (event.payload as { projectId?: string; callbackId?: string }) || {};
        if (!payload.callbackId || !payload.projectId) break;
        const callback = this.deleteCallbacks.get(payload.callbackId);
        if (callback) {
          callback(payload.projectId);
        }
        break;
      }
      default:
        break;
    }
  }

  async removeDeviceFromCloud(device: UserDevice): Promise<EmptyResponse> {
        try {
            await CloudUrl.deleteAllFiles(CloudUrl.deviceBaseUrl(device.id));
            this.notifyStatusChange();
        } catch (error) {
            EmptyResponse.sendError(`sync: failed to delete device data. error: ${error}`);
        }
        return EmptyResponse.success();
    }

    async removeUserFromCloud(): Promise<EmptyResponse> {
        try {
            await CloudUrl.deleteAllFiles(CloudUrl.userBaseUrl());
            this.notifyStatusChange();
        } catch (error) {
            EmptyResponse.sendError(`sync: failed to delete user data. error: ${error}`);
        }
        return EmptyResponse.success();
    }

        ////////////////////////////////
    ///////// realtime db //////////
    ////////////////////////////////
    //MARK: realtime db

    private get baseChangesReference(): DatabaseReference {
        // @ts-ignore
        return ref(getDatabase(), `/${CloudUrl.baseUrl()}/cloud_storage/users/${this.userId}`);
    }

    private get baseProjectsChangesReference(): DatabaseReference {
        // @ts-ignore
        return ref(getDatabase(), `${this.baseChangesReference._path.toString()}/projectUpdates`);
    }

    private get baseFoldersChangesReference(): DatabaseReference {
        // @ts-ignore
        return ref(getDatabase(), `${this.baseChangesReference._path.toString()}/folders`);
    }

    private get baseRecycleBinChangesReference(): DatabaseReference {
        // @ts-ignore
        return ref(getDatabase(), `${this.baseChangesReference._path.toString()}/features/recycle_bin/data`);
    }

    private get deviceLogReference(): DatabaseReference | undefined {
        // TODO DROR: fix this
        const deviceId = "web" 
        // const deviceId = Device.identifier; // Make sure Device.identifier is defined
        // @ts-ignore
        return deviceId ? ref(getDatabase(), `/logs/devices/${deviceId}`) : undefined;
    }

    startListenToChanges() {
        off(this.baseRecycleBinChangesReference);
        off(this.baseProjectsChangesReference);
        off(this.baseFoldersChangesReference);

        let that = this;
        onValue(this.baseProjectsChangesReference, async (snapshot: DataSnapshot) => {
            const dict = snapshot.val();
            if (dict && dict.device && dict.device !== UserDevice.current.id) {
                that.logWithTimestamp(`sync: did received status change on other device: ${dict.device}`);
                that.delegate?.cloudManagerDidDetectMetaDataChange();
            } else {
                that.logWithTimestamp("sync: did receive an empty status on other devices");
            }
        });

        onValue(this.baseFoldersChangesReference, async (snapshot: DataSnapshot) => {
            const dict = snapshot.val();
            if (dict && dict.device && dict.device !== UserDevice.current.id) {
                that.logWithTimestamp(`sync: did received folders change on other device: ${dict.device}`);
                const folders = await that.fetchFoldersMetaDataArray();
                that.delegate?.cloudManagerDidReceivedCloudFolders(folders?.value || []);
            } else {
                that.delegate?.cloudManagerDidReceivedCloudFolders([]);
                that.logWithTimestamp("sync: did receive an empty folders update on other devices");
            }
        });

        onValue(this.baseRecycleBinChangesReference, async (snapshot: DataSnapshot) => {
            const dict = snapshot.val();
            that.delegate?.cloudManagerDidReceivedRecycleBinItems(dict || {});
        });
    }

    notifyStatusChange() {
        BtUtils.throttle(300, async () => {
            try {
                await set(this.baseProjectsChangesReference, {
                    lastUpdate: Date.now() / 1000,
                    device: UserDevice.current.id
                });
            } catch (error) {
                console.warn(`sync: failed to mark real time database with the change.error: ${error}`);
            }
        }, "cloudManager_notifyOtherDevices");
    }

    notifyFoldersChange() {
        BtUtils.throttle(300, async () => {
            try {
                await set(this.baseFoldersChangesReference, {
                    lastUpdate: Date.now() / 1000,
                    device: UserDevice.current.id
                });
            } catch (error) {
                console.warn(`sync: failed to mark real time database with the folders change. error: ${error}`);
            }
        }, "cloudManager_notifyOtherDevices_foldersChange");
    }
    
  // Delegate pass-throughs
  cloudManagerDidUploadMetaData(): void {
    this.delegate?.cloudManagerDidUploadMetaData();
  }
  cloudManagerDidDetectMetaDataChange(): void {
    this.delegate?.cloudManagerDidDetectMetaDataChange();
  }
  cloudManagerDidStartUploadProject(callbackId: string): void {
    this.delegate?.cloudManagerDidStartUploadProject?.(callbackId);
  }
  cloudManagerDidReceivedCloudFolders(folders: any[]): void {
    this.delegate?.cloudManagerDidReceivedCloudFolders(folders);
  }
  cloudManagerDidReceivedRecycleBinItems(items: object): void {
    this.delegate?.cloudManagerDidReceivedRecycleBinItems(items);
  }

  async logWithTimestamp(message: string, value?: any): Promise<void> {
    await this.sendRequest("logWithTimestamp", message, value);
  }

  async setup(): Promise<void> {
    const state = await this.sendRequest("setup");
    if (state && typeof state === "object") {
      
    }
    await this.ensureEventSubscription();
  }

  async setFirebaseToken(token: string, username?: string): Promise<string | null> {
    void super.setFirebaseToken(token, username).catch((error) => {
      console.error("sync: failed to set Firebase token in window context", error);
    });
    const result = await this.sendRequest("setFirebaseToken", token, username);
    return result;
  }

  async logout(): Promise<void> {
    await this.sendRequest("logout");
    super.logout();
  }

  async cancelAllUploads(): Promise<void> {
    await this.sendRequest("cancelAllUploads");
  }

  async didHaveSync() {
    return await this.sendRequest("didHaveSync");
  }

  async downloadCloudMetadata() {
    return await this.sendRequest("downloadCloudMetadata");
  }

  async downloadCloudUnsafeFeed() {
    return await this.sendRequest("downloadCloudUnsafeFeed");
  }

  async cancelFetchingProject(draftId: string) {
    return await this.sendRequest("cancelFetchingProject", draftId);
  }

  async fetchProject(draftId: string, device: any, process: any, latestOnly?: boolean) {
    return await this.sendRequest("fetchProject", draftId, device, process, latestOnly);
  }

  async deleteProjects(projectIds: string[], callback?: (id: string) => Promise<void>) {
    const callbackId = callback ? nextId() : undefined;

    if (callbackId && callback) {
      await this.ensureEventSubscription();
      this.deleteCallbacks.set(callbackId, callback);
    }

    try {
      return await this.sendRequest("deleteProjects", projectIds, callbackId);
    } finally {
      if (callbackId) {
        this.deleteCallbacks.delete(callbackId);
      }
    }
  }

  async uploadProject(project: any, projectUrl: string, didStartBlock?: () => void) {
    const callbackId = didStartBlock ? nextId() : undefined;

    if (callbackId && didStartBlock) {
      await this.ensureEventSubscription();
      this.uploadStartCallbacks.set(callbackId, didStartBlock);
    }

    return await this.sendRequest("uploadProject", project, projectUrl, callbackId);
  }

  async fetchFoldersMetaDataArray(): Promise<CloudResponse<FolderObject[]>> {
    return await this.sendRequest("fetchFoldersMetaDataArray");
  }

  async uploadProjectsMetaDataArray(versions: any, allFeed?: any) {
    return await this.sendRequest("uploadProjectsMetaDataArray", versions, allFeed);
  }

  async deleteProject(projectId: string) {
    return await this.sendRequest("deleteProject", projectId);
  }

  async downloadFontsFileNames() {
    return await this.sendRequest("downloadFontsFileNames");
  }
  
  isDownloading(projectId: string): Promise<boolean> {
    return this.sendRequest("isDownloading", projectId);
  }

  progressPercent(projectId: string): Promise<number> {
    return this.sendRequest("progressPercent", projectId);
  }

}
